import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

// A long-lived SPA tab survives every redeploy untouched — the owner kept an
// open tab for days and concluded the site "never changes". Watch the served
// index.html for a new entry chunk (on refocus and every few minutes) and
// offer a one-click reload when the build behind the tab goes stale.
const CHECK_INTERVAL_MS = 5 * 60 * 1000
const ENTRY_RE = /assets\/index-[\w-]+\.js/

export const useFreshBuildWatcher = () => {
  const { t } = useTranslation()
  const notified = useRef(false)

  useEffect(() => {
    const currentEntry = document
      .querySelector('script[src*="/assets/index-"]')
      ?.getAttribute('src')
      ?.replace(/^\//, '')
    // Dev server has no hashed entry — nothing to watch.
    if (!currentEntry) return

    const check = async () => {
      if (notified.current || document.visibilityState === 'hidden') return
      try {
        const response = await fetch('/', { cache: 'no-store' })
        if (!response.ok) return
        const html = await response.text()
        const served = html.match(ENTRY_RE)?.[0]
        if (!served || served === currentEntry) return
        notified.current = true
        toast.info(t('update_available'), {
          autoClose: false,
          closeOnClick: false,
          onClick: () => window.location.reload(),
        })
      } catch {
        // offline or transient — try again next tick
      }
    }

    const timer = setInterval(check, CHECK_INTERVAL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') check()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [t])
}
